package com.neurobs.ccabserver.administrator

import com.neurobs.js.ccab.UserManager
import org.scalajs.dom.{console, document, window, Event}
import org.scalajs.dom.window.localStorage

import scala.concurrent.{ExecutionContext, Future}
import scala.concurrent.duration._
import scala.scalajs.js
import scala.scalajs.js.JSConverters._
import scala.util.Try
import scala.util.control.NonFatal

/**
 * Monitors user activity and logs out after a period of inactivity.
 *
 * Activity is tracked across browser tabs by writing a timestamp to localStorage
 * whenever an interaction event occurs. Each tab periodically reads that
 * timestamp and logs out if the timeout has been exceeded.
 *
 * On activity, the monitor can also refresh Cognito tokens at a slower interval
 * so that server calls are less likely to fail with an expired id token.
 *
 * Token refresh and logout are coordinated across tabs with the Web Locks API
 * when available, so that only one tab performs each operation. Without Web
 * Locks, the shared timestamps still dedup best-effort.
 *
 * @param userManager Manager used to check login state and perform logout.
 * @param timeout Inactivity timeout.
 * @param checkInterval How often to check for inactivity.
 * @param activityStorageKey localStorage key used for the shared timestamp.
 * @param minActivityInterval Minimum time between activity timestamp updates.
 * @param tokenRefreshInterval Minimum time between token refresh attempts on activity.
 * @param tokenRefreshStorageKey localStorage key used for the shared token refresh timestamp.
 * @param logoutStorageKey localStorage key used for the shared logout timestamp.
 * @param logoutDedupWindow A logout whose timestamp is within this window of now
 *                          (in either direction) is treated as already handling the
 *                          current timeout, so other tabs skip their own logout.
 */
class InactivityMonitor(
    userManager: UserManager,
    timeout: FiniteDuration = 180.minutes,
    checkInterval: FiniteDuration = 1.minute,
    activityStorageKey: String = "ccab_last_activity",
    minActivityInterval: FiniteDuration = 30.seconds,
    tokenRefreshInterval: FiniteDuration = 60.minutes,
    tokenRefreshStorageKey: String = "ccab_last_token_refresh",
    logoutStorageKey: String = "ccab_last_logout",
    logoutDedupWindow: FiniteDuration = 10.seconds,
    debugOutput: Boolean = false
)(implicit ec: ExecutionContext) {
    private val timeoutMillis: Double = timeout.toMillis
    private val checkIntervalMillis: Int = checkInterval.toMillis.toInt
    private val minActivityIntervalMillis: Int = minActivityInterval.toMillis.toInt
    private val tokenRefreshIntervalMillis: Double = tokenRefreshInterval.toMillis
    private val logoutDedupWindowMillis: Double = logoutDedupWindow.toMillis

    private var lastActivity: Double = js.Date.now()
    private var checkHandle: Option[Int] = None
    private var logoutInProgress: Boolean = false
    private var lastActivityUpdateTime: Double = 0
    private var active: Boolean = false
    private var hadUser: Boolean = false

    private val activityEventNames = Seq("mousedown", "mousemove", "keydown", "touchstart", "scroll")
    private val visibilityEventName = "visibilitychange"

    private val onActivityHandler: js.Function1[Event, Unit] = onActivity _
    private val onVisibilityChangeHandler: js.Function1[Event, Unit] = onVisibilityChange _
    private val checkHandler: js.Function0[Unit] = () => check()

    private val tokenRefreshLockName = "ccab_token_refresh"
    private val logoutLockName = "ccab_logout"

    def debugOut(message: String): Unit = {
        if (debugOutput) {
            console.log(s"InactivityMonitor: $message")
        }
    }

    start()

    private def lockManager: Option[InactivityMonitor.LockManager] = {
        val locks = window.navigator.asInstanceOf[js.Dynamic].locks
        debugOut(s"lockManager: locks = $locks")
        if (js.isUndefined(locks) || locks == null) None
        else Some(locks.asInstanceOf[InactivityMonitor.LockManager])
    }

    /**
     * Runs body while holding the named Web Lock, so that only one tab executes it
     * at a time. The lock is held until body's Future completes, and is released
     * automatically by the browser if this tab is killed while holding it. Falls
     * back to running unprotected when the Web Locks API is unavailable.
     */
    private def withLock(name: String)(body: => Future[Unit]): Unit = {
        def guarded(): js.Promise[Any] = {
            val result = try body
            catch {
                case NonFatal(e) => Future.failed(e)
            }
            result
                .recover { case NonFatal(e) => console.warn(s"InactivityMonitor: $name failed: ${e.getMessage}") }
                .toJSPromise
                .asInstanceOf[js.Promise[Any]]
        }
        lockManager match {
            case Some(locks) => locks.request(name, (_: InactivityMonitor.Lock) => guarded())
            case None => guarded()
        }
    }

    def stop(): Unit = {
        if (active) {
            active = false
            checkHandle.foreach(window.clearInterval)
            checkHandle = None
            activityEventNames.foreach(document.removeEventListener(_, onActivityHandler))
            document.removeEventListener(visibilityEventName, onVisibilityChangeHandler)
        }
    }

    private def start(): Unit = {
        active = true
        activityEventNames.foreach(document.addEventListener(_, onActivityHandler))
        document.addEventListener(visibilityEventName, onVisibilityChangeHandler)
        checkHandle = Some(window.setInterval(checkHandler, checkIntervalMillis))
        updateActivity(js.Date.now())
    }

    private def onActivity(e: Event): Unit = {
        val now = js.Date.now()
        if (now - lastActivityUpdateTime > minActivityIntervalMillis) {
            debugOut(s"onActivity: event ${e.`type`}, delta = ${now - lastActivityUpdateTime}")
            // Check for an elapsed timeout before recording this as activity. When a
            // hidden tab is reactivated, input events can arrive before the
            // visibilitychange handler (and while the interval timer was throttled),
            // so without this the first activity would reset the clock and the user
            // would not be logged out despite the timeout having passed.
            check()

            updateActivity(now)
            maybeRefreshTokens(now)
        }
    }

    private def maybeRefreshTokens(now: Double): Unit = {
        debugOut(s"maybeRefreshTokens: now = $now")
        if (userManager.hasUser && tokenRefreshDue(now)) {
            debugOut(s"maybeRefreshTokens: token refresh due, acquiring lock")
            withLock(tokenRefreshLockName) {
                // Re-check inside the lock: a tab that queued behind the winner
                // sees the fresh timestamp and skips. The timestamp is written
                // only after a successful refresh, so a tab killed mid-refresh
                // does not suppress retries by other tabs.
                if (!tokenRefreshDue(js.Date.now())) {
                    debugOut(s"maybeRefreshTokens: token refresh no longer due, skipping")
                    Future.successful(())
                } else {
                    userManager.update(forceRefresh = true) map {
                        case Right(_) =>
                            localStorage.setItem(tokenRefreshStorageKey, js.Date.now().toString)
                            debugOut(s"maybeRefreshTokens: token refresh succeeded, updated timestamp")
                        case _ =>
                            debugOut(s"maybeRefreshTokens: token refresh failed, leaving timestamp stale")
                            // Refresh failed (e.g. network error); leave the shared
                            // timestamp stale so another tab retries promptly.
                    }
                }
            }
        }
    }

    private def tokenRefreshDue(now: Double): Boolean = {
        readStoredTimestamp(tokenRefreshStorageKey) match {
            case Some(lastRefresh) => now - lastRefresh > tokenRefreshIntervalMillis
            case None =>
                localStorage.setItem(tokenRefreshStorageKey, now.toString)
                false
        }
    }

    private def onVisibilityChange(e: Event): Unit = {
        if (!document.hidden) {
            debugOut(s"onVisibilityChange: event ${e.`type`}, now = ${js.Date.now()}")
            check()
        }
    }

    private def updateActivity(now: Double): Unit = {
        if (active) {
            debugOut(s"updateActivity: now = $now, lastActivity = $lastActivity")
            lastActivity = now
            lastActivityUpdateTime = now
            localStorage.setItem(activityStorageKey, now.toString)
        }
    }

    private def readStoredTimestamp(key: String): Option[Double] = {
        // NB: localStorage.getItem returns null (not undefined) for missing keys;
        // the null check must happen before touching the value.
        val value = localStorage.getItem(key)
        if (value == null) None
        else Try(value.toDouble).toOption
    }

    private def check(): Unit = {
        if (!active) {
            return
        }

        val hasUserNow = userManager.hasUser
        if (hasUserNow && !hadUser) {
            // User just logged in; reset the activity baseline so they don't
            // get logged out immediately based on pre-login activity, and clear
            // any stale logout guard from a previous session.
            logoutInProgress = false
            updateActivity(js.Date.now())
        }
        hadUser = hasUserNow

        if (!hasUserNow) {
            debugOut(s"check: delta = ${js.Date.now() - lastActivity}, hasUser = $hasUserNow, logoutInProgress = $logoutInProgress")
            return
        }

        readStoredTimestamp(activityStorageKey).foreach { timestamp =>
            if (timestamp > lastActivity) {
                lastActivity = timestamp
            }
        }
        debugOut(s"check: delta = ${js.Date.now() - lastActivity}, hasUser = $hasUserNow, logoutInProgress = $logoutInProgress")

        val inactiveMillis = js.Date.now() - lastActivity
        if (inactiveMillis > timeoutMillis && !logoutInProgress) {
            logoutInProgress = true
            debugOut(s"check: inactivity timeout exceeded ($inactiveMillis ms), logging out")
            withLock(logoutLockName) {
                // Skip if another tab completed a logout within the dedup window
                // around now; concurrent inactivity timeouts across tabs produce
                // stamps clustered together in time, so a nearby stamp means this
                // timeout is already being handled. A stamp from an earlier,
                // separate timeout falls outside the window and does not suppress
                // this one. The stamp is written only after logout completes, so a
                // tab killed mid-logout does not block retries.
                val now = js.Date.now()
                val logoutAlreadyHandled = readStoredTimestamp(logoutStorageKey)
                    .exists(t => Math.abs(t - now) <= logoutDedupWindowMillis)
                if (logoutAlreadyHandled) {
                    debugOut(s"check: logout already handled by another tab, skipping")
                    Future.successful(())
                } else {
                    debugOut(s"check: performing logout")
                    userManager.logout() map { _ =>
                        localStorage.setItem(logoutStorageKey, js.Date.now().toString)
                    }
                }
            }
        }
    }
}

object InactivityMonitor {
    @js.native
    trait Lock extends js.Object {
        val name: String = js.native
    }

    @js.native
    trait LockManager extends js.Object {
        def request(name: String, callback: js.Function1[Lock, js.Promise[Any]]): js.Promise[Any] = js.native
    }
}
